import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Area, AreaChart } from 'recharts';
import { ChevronRight, Clock, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, User, Calendar, Timer, Gamepad2, SkipBack, SkipForward, ArrowLeft, ArrowRight, Home } from 'lucide-react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import { useTranslation } from "react-i18next";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { GENERAL } from "../general/General";

const UnifiedChessAnalytics = ({ updateBackground }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('confidence');
  const [moveIndex, setMoveIndex] = useState(0);
  const [bestMoveFen, setBestMoveFen] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gameInfo, setGameInfo] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [chatAnalysis, setChatAnalysis] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { gameId, pgn, playerColor, gameTeamType, gameInfoFromBackend } = location.state || {};

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    updateBackground({
      image: GENERAL.BACKGROUND,
    });
  }, [updateBackground]);

  // WebSocket data fetching logic
  useEffect(() => {
    if (gameInfoFromBackend) {
      let raw = gameInfoFromBackend.analytics;
      let obj = typeof raw === "string" ? JSON.parse(raw) : raw;
      setGameInfo(gameInfoFromBackend);
      setAnalysis(obj);

      // FIXED: Handle chatAnalytics properly for game data
      let chatAnalyticsRaw = gameInfoFromBackend.chatAnalytics;
      let chatAnalyticsObj = typeof chatAnalyticsRaw === "string" ? JSON.parse(chatAnalyticsRaw) : chatAnalyticsRaw;
      setChatAnalysis(chatAnalyticsObj);

      setLoading(false);
      return;
    }

    if (!pgn && !gameId) return;

    setLoading(true);
    const handleWebSocketEvent = (event) => {
      if (
        event.type === "message" &&
        event.message.messageType === "analitics_result"
      ) {
        let raw = event.message.gameInfo.analytics;
        let obj = typeof raw === "string" ? JSON.parse(raw) : raw;
        setGameInfo(event.message.gameInfo);
        
        // FIXED: Handle chatAnalytics properly for game data
        let chatAnalyticsRaw = event.message.gameInfo.chatAnalytics;
        let chatAnalyticsObj = typeof chatAnalyticsRaw === "string" ? JSON.parse(chatAnalyticsRaw) : chatAnalyticsRaw;
        setChatAnalysis(chatAnalyticsObj);
        
        setAnalysis(obj);
        setLoading(false);
      }
    };
    addListener(handleWebSocketEvent);

    const generalMessage = { ...generalMessageTemplate };
    generalMessage.language = localStorage.getItem("i18nextLng") || "en"; 
    generalMessage.messageType = "analitics_request";
    generalMessage.gameInfo.gameId = gameId;
    generalMessage.gameInfo.basicGameHistory = localStorage.getItem("moveHistory");
    generalMessage.gameInfo.color = playerColor;
    generalMessage.gameInfo.pgn = pgn; 
    generalMessage.gameInfo.gameTeamType = gameTeamType;
    sendMessage(generalMessage);
 
    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, [gameId, gameInfoFromBackend, gameTeamType, pgn, playerColor]);

  // Data extraction - supporting both chatAnalytics and lichess/stockfish structure
  const lichess = analysis?.lichess || null;
  const stockfish = analysis?.stockfish || null;
  const analysisType = 'stockfish';
  const current = analysisType === "lichess" ? lichess : stockfish;

  // Data extraction - FIXED: Better fallback logic for move data
  const chatAnalytics = chatAnalysis;
  const gameMetadata = chatAnalytics?.gameMetadata || {};
  const statistics = chatAnalytics?.statistics || {};
  const chartMoveData = chatAnalytics?.moveData || []; // For charts
  const analysisData = chatAnalytics?.analysisData || [];
  const errors = chatAnalytics?.errors || [];
  const keyMoments = chatAnalytics?.keyMoments || [];
  const conclusions = chatAnalytics?.conclusions || {};
  
  // Try multiple possible paths for navigation move data
  const navigationMoveData = analysis?.stockfish?.moves || 
                            analysis?.moves || 
                            analysis?.analysis || 
                            chartMoveData || // Fallback to chatAnalytics moveData
                            [];
  
  // Debug logging
  // console.log('Analysis structure:', analysis);
  // console.log('Navigation move data length:', navigationMoveData?.length);
  // console.log('Chart move data length:', chartMoveData?.length);
  // console.log('First navigation move:', navigationMoveData?.[0]);
  // console.log('First chart move:', chartMoveData?.[0]);

  // Move positions generation - FIXED: Handle actual moveObj structure
  const movePositions = useMemo(() => {
    // Add starting position first
    const positions = [{ 
      fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1", 
      san: null,
      move: 0,
      halfMoveIndex: 0
    }];

    //console.log('Building positions from navigationMoveData:', navigationMoveData?.length);

    if (navigationMoveData && navigationMoveData.length > 0) {
      // Build game positions by playing moves sequentially
      const game = new Chess();
      
      navigationMoveData.forEach((moveObj, index) => {
        //console.log(`Processing move ${index}:`, moveObj);
        
        // Extract UCI move from the actual structure
        const playedUci = moveObj.playedUci;
        
        if (playedUci && playedUci.length >= 4) {
          try {
            // Parse UCI notation (e.g., "d2d4")
            const from = playedUci.substring(0, 2);
            const to = playedUci.substring(2, 4);
            const promotion = playedUci.length === 5 ? playedUci[4] : undefined;
            
            // Make the move on the chess board
            const move = game.move({
              from: from,
              to: to,
              promotion: promotion
            });
            
            if (move) {
              positions.push({
                fen: game.fen(),
                san: move.san, // Chess.js provides the SAN notation
                move: Math.floor(index / 2) + 1,
                halfMoveIndex: index + 1,
                player: index % 2 === 0 ? 'white' : 'black',
                eval: moveObj.evalAfter || 0,
                evalBefore: moveObj.evalBefore || 0,
                evalBest: moveObj.evalBest || 0,
                bestMoveUci: moveObj.bestMoveUci || null,
                judgment: moveObj.judgment || 'Good',
                comment: moveObj.comment || '',
                playedUci: playedUci,
                ply: moveObj.ply || index
              });
              
              //console.log(`Added position ${index + 1}: ${move.san} -> ${game.fen()}`);
            } else {
              //console.error(`Failed to make move: ${playedUci}`);
            }
          } catch (error) {
            //console.error(`Error processing move ${index} (${playedUci}):`, error);
          }
        } else {
          //console.error(`Invalid UCI move at index ${index}:`, playedUci);
        }
      });
    }

    // Fallback: If still no positions and we have chatAnalytics moveData
    if (positions.length === 1 && chartMoveData && chartMoveData.length > 0) {
      console.log('Falling back to chartMoveData');
      chartMoveData.forEach((moveObj, index) => {
        if (moveObj.fen && moveObj.san) {
          positions.push({
            fen: moveObj.fen,
            san: moveObj.san,
            move: moveObj.move || Math.floor(index / 2) + 1,
            halfMoveIndex: index + 1,
            player: (index % 2) === 0 ? 'white' : 'black',
            eval: 0,
            bestMoveUci: null
          });
        }
      });
    }

    // Final fallback: Use current (lichess/stockfish legacy format)
    if (positions.length === 1 && current) {
      console.log('Falling back to current moves');
      const game = new Chess();
      let halfMoveCount = 1;

      if (typeof current.moves === "string") {
        const sanArr = current.moves.trim().split(/\s+/);
        sanArr.forEach((move) => {
          const m = game.move(move, { sloppy: true });
          positions.push({ 
            fen: game.fen(), 
            san: m ? m.san : move, 
            move: Math.floor((halfMoveCount - 1) / 2) + 1, 
            halfMoveIndex: halfMoveCount,
            player: halfMoveCount % 2 === 1 ? 'white' : 'black'
          });
          halfMoveCount++;
        });
      } else if (Array.isArray(current.moves)) {
        current.moves.forEach((m) => {
          let playedSan = m.playedSan;

          if (playedSan) {
            const mm = game.move(playedSan, { sloppy: true });
            positions.push({ 
              fen: game.fen(), 
              san: mm ? mm.san : playedSan, 
              move: Math.floor((halfMoveCount - 1) / 2) + 1, 
              halfMoveIndex: halfMoveCount,
              player: halfMoveCount % 2 === 1 ? 'white' : 'black'
            });
            halfMoveCount++;
          } else if (m.playedUci) {
            const from = m.playedUci.substring(0, 2);
            const to = m.playedUci.substring(2, 4);
            const piece = game.get(from);

            const isPawnPromotion =
              piece?.type === "p" && (to[1] === "1" || to[1] === "8");

            const moveObj = game.move({
              from,
              to,
              promotion:
                m.playedUci.length === 5
                  ? m.playedUci[4]
                  : isPawnPromotion
                  ? "q"
                  : undefined,
            });

            positions.push({
              fen: game.fen(),
              san: moveObj ? moveObj.san : m.playedUci,
              move: Math.floor((halfMoveCount - 1) / 2) + 1,
              halfMoveIndex: halfMoveCount,
              player: halfMoveCount % 2 === 1 ? 'white' : 'black'
            });
            halfMoveCount++;
          }
        });
      }
    }

    console.log('Final positions count:', positions.length);
    console.log('First few positions:', positions.slice(0, 3));
    return positions;
  }, [navigationMoveData, chartMoveData, current]);

  // Analysis array - FIXED: Use correct moveObj structure
  const analysisArr = useMemo(() => {
    // If we have chatAnalytics analysisData, use it
    if (analysisData && analysisData.length > 0) {
      return analysisData;
    }

    // If we have navigationMoveData with the correct structure, use it
    if (navigationMoveData && navigationMoveData.length > 0) {
      return navigationMoveData.map((moveObj, index) => ({
        move: index + 1,
        san: '', // Will be filled by chess.js when we process the move
        eval: moveObj.evalAfter || 0,
        evalBefore: moveObj.evalBefore || 0,
        evalBest: moveObj.evalBest || 0,
        judgment: moveObj.judgment || 'Good',
        comment: moveObj.comment || '',
        bestMoveUci: moveObj.bestMoveUci || null,
        playedUci: moveObj.playedUci,
        halfMoveIndex: index,
        ply: moveObj.ply || index
      }));
    }

    // Otherwise use original logic from LichessAnalysisPage
    if (!current) return [];
    if (Array.isArray(current.analysis)) return current.analysis;
    if (Array.isArray(current.moves)) return current.moves;
    return [];
  }, [current, analysisData, navigationMoveData]);

  // Game info extraction
  const opening = lichess?.opening || gameMetadata.opening || {};
  const result = current?.status ? t(current.status) : gameMetadata.result || "*";
  
  // Player names
  const whitePlayer = gameInfo?.mainWhite || stockfish?.mainWhite || gameMetadata.whitePlayer || t("White");
  const blackPlayer = gameInfo?.mainBlack || stockfish?.mainBlack || gameMetadata.blackPlayer || t("Black");

  // Statistics
  const w_acpl = lichess?.players?.white?.analysis?.acpl;
  const b_acpl = lichess?.players?.black?.analysis?.acpl;
  const whiteAccuracy = statistics.whiteAccuracy || 0;
  const blackAccuracy = statistics.blackAccuracy || 0;
  // FIXED: Total moves from analytics for navigation, other stats from chatAnalytics
  const totalMoves = navigationMoveData?.length || statistics.totalMoves || gameMetadata.totalMoves || 0;

  // Move quality counts
  let bestMoveCount = 0, blunderCount = 0, mistakeCount = 0, inaccCount = 0, goodCount = 0;
  
  if (statistics.summary) {
    // Use chatAnalytics summary
    bestMoveCount = (statistics.summary.w_bestMoves || 0) + (statistics.summary.b_bestMoves || 0);
    goodCount = (statistics.summary.w_goodMoves || 0) + (statistics.summary.b_goodMoves || 0);
    inaccCount = (statistics.summary.w_inaccuracies || 0) + (statistics.summary.b_inaccuracies || 0);
    mistakeCount = (statistics.summary.w_mistakes || 0) + (statistics.summary.b_mistakes || 0);
    blunderCount = (statistics.summary.w_blunders || 0) + (statistics.summary.b_blunders || 0);
  } else {
    // Use original logic from LichessAnalysisPage
    for (const a of analysisArr) {
      const judgmentName = a.judgment?.name || a.judgment || a.judgement;
      if (judgmentName === "Blunder") blunderCount++;
      if (judgmentName === "Mistake") mistakeCount++;
      if (judgmentName === "Inaccuracy") inaccCount++;
      if (judgmentName === "Good") goodCount++;
      if (judgmentName === "Best") bestMoveCount++;
    }
  }

  const plyCount = movePositions?.length ? movePositions.length - 1 : 0;
  const accuracyPercent = plyCount ? Math.round((bestMoveCount / plyCount) * 100) : 100;

  // Calculate board size for mobile - same as LichessAnalysisPage
  const boardWidth = isMobile ? Math.min(window.innerWidth * 0.9, 360) : 400;

  const currentPosition = movePositions?.[moveIndex] || movePositions?.[0] || {};
  const currentFen = bestMoveFen || currentPosition.fen || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

  // FIXED: Navigation functions with better logging
  const onPrev = () => {
    const newIndex = Math.max(0, moveIndex - 1);
    console.log('Previous: from', moveIndex, 'to', newIndex);
    setMoveIndex(newIndex);
    setBestMoveFen(null);
    setArrows([]);
  };

  const onNext = () => {
    const maxIndex = (movePositions?.length || 1) - 1;
    const newIndex = Math.min(maxIndex, moveIndex + 1);
    console.log('Next: from', moveIndex, 'to', newIndex, 'max:', maxIndex);
    setMoveIndex(newIndex);
    setBestMoveFen(null);
    setArrows([]);
  };

  const onFirst = () => {
    console.log('First: from', moveIndex, 'to 0');
    setMoveIndex(0);
    setBestMoveFen(null);
    setArrows([]);
  };

  const onLast = () => {
    const lastIndex = (movePositions?.length || 1) - 1;
    console.log('Last: from', moveIndex, 'to', lastIndex);
    setMoveIndex(lastIndex);
    setBestMoveFen(null);
    setArrows([]);
  };

  // FIXED: Add missing handleChartClick function
  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedData = data.activePayload[0].payload;
      if (clickedData && typeof clickedData.move === 'number') {
        // Convert move number to half-move index
        const halfMoveIndex = (clickedData.move - 1) * 2 + (clickedData.player === 'black' ? 1 : 0);
        const targetIndex = Math.min(halfMoveIndex, (movePositions?.length || 1) - 1);
        console.log('Chart click: move', clickedData.move, 'player', clickedData.player, 'targetIndex', targetIndex);
        setMoveIndex(targetIndex);
        setBestMoveFen(null);
        setArrows([]);
      }
    }
  };

  // FIXED: Add missing CustomTooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 p-3 rounded-lg border border-gray-600 shadow-lg">
          <p className="text-white font-medium">{`${t("Move")} ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.name}: ${entry.value}${entry.dataKey.includes('Accuracy') || entry.dataKey.includes('Confidence') ? '%' : 
                entry.dataKey.includes('Time') ? 's' : ''}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Get FEN after best move - Updated for stockfish move structure
  function getFenAfterBestMove(rowIdx) {
    const analysis = analysisArr[rowIdx];
    const bestMoveUci = analysis?.bestMoveUci;
    
    if (!bestMoveUci) return null;
    
    // Get the FEN before this move was played
    const prevFen = rowIdx > 0 ? movePositions[rowIdx]?.fen : "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
    
    try {
      const chess = new Chess(prevFen);
      const move = chess.move({
        from: bestMoveUci.substring(0, 2),
        to: bestMoveUci.substring(2, 4),
        promotion: bestMoveUci.length === 5 ? bestMoveUci[4] : undefined,
      });
      if (move) return chess.fen();
    } catch (e) {
      console.error('Error calculating best move FEN:', e);
      // Fallback: try to use the position before the current move
      try {
        const chess = new Chess(movePositions[rowIdx - 1]?.fen || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
        const move = chess.move({
          from: bestMoveUci.substring(0, 2),
          to: bestMoveUci.substring(2, 4),
          promotion: bestMoveUci.length === 5 ? bestMoveUci[4] : undefined,
        });
        if (move) return chess.fen();
      } catch (e2) {
        console.error('Fallback FEN calculation also failed:', e2);
      }
    }
    return null;
  }

  // Chart rendering - Use chatAnalytics moveData for charts
  const renderChart = () => {
    // Use chartMoveData from chatAnalytics for charts
    if (!chartMoveData || chartMoveData.length === 0) return null;

    const chartConfig = {
      confidence: {
        lines: [
          { dataKey: 'whiteConfidence', stroke: '#3b82f6', name: t('White Confidence') },
          { dataKey: 'blackConfidence', stroke: '#ef4444', name: t('Black Confidence') }
        ]
      },
      stress: {
        areas: [
          { dataKey: 'whiteStress', stackId: 'white', stroke: '#3b82f6', fill: 'blue', name: t('White Stress') },
          { dataKey: 'blackStress', stackId: 'black', stroke: '#ef4444', fill: 'black', name: t('Black Stress') }
        ]
      },
      time: {
        bars: [
          { dataKey: 'whiteTime', fill: '#3b82f6', name: t('White Time (sec)') },
          { dataKey: 'blackTime', fill: '#ef4444', name: t('Black Time (sec)') }
        ]
      },
      accuracy: {
        lines: [
          { dataKey: 'whiteAccuracy', stroke: '#3b82f6', name: t('White Accuracy (%)') },
          { dataKey: 'blackAccuracy', stroke: '#ef4444', name: t('Black Accuracy (%)') }
        ]
      }
    };

    const config = chartConfig[activeTab];
    if (!config) return null;

    const commonProps = {
      data: chartMoveData, // Use chatAnalytics moveData for charts
      onClick: handleChartClick,
      children: [
        <CartesianGrid key="grid" strokeDasharray="3 3" stroke="#374151" />,
        <XAxis key="xaxis" dataKey="move" stroke="#9ca3af" />,
        <YAxis key="yaxis" stroke="#9ca3af" />,
        <Tooltip key="tooltip" content={<CustomTooltip />} />,
        <Legend key="legend" />
      ]
    };

    switch (activeTab) {
      case 'confidence':
      case 'accuracy':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <LineChart {...commonProps}>
              {commonProps.children}
              {config.lines.map((line, index) => (
                <Line 
                  key={index} 
                  type="monotone" 
                  dataKey={line.dataKey} 
                  stroke={line.stroke} 
                  strokeWidth={3} 
                  name={line.name} 
                  dot={{ fill: line.stroke, strokeWidth: 2, r: 4, cursor: 'pointer' }}
                  activeDot={{ r: 6, cursor: 'pointer' }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      case 'stress':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <AreaChart {...commonProps}>
              {commonProps.children}
              {config.areas.map((area, index) => (
                <Area 
                  key={index} 
                  type="monotone" 
                  dataKey={area.dataKey} 
                  stackId={area.stackId} 
                  stroke={area.stroke} 
                  fill={area.fill} 
                  fillOpacity={area.fillOpacity || 0.6} 
                  name={area.name} 
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'time':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <BarChart {...commonProps}>
              {commonProps.children}
              {config.bars.map((bar, index) => (
                <Bar 
                  key={index} 
                  dataKey={bar.dataKey} 
                  fill={bar.fill} 
                  name={bar.name}
                  cursor="pointer"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  // Utility functions
  const getSeverityColor = (severity) => {
    const colors = { 1: '#fbbf24', 2: '#f97316', 3: '#ef4444', 4: '#dc2626' };
    return colors[severity] || '#6b7280';
  };

  const getSignificanceColor = (significance) => {
    const colors = {
      critical: 'bg-red-900 text-red-200',
      tactical: 'bg-blue-900 text-blue-200',
      strategic: 'bg-green-900 text-green-200',
      decisive: 'bg-purple-900 text-purple-200',
      phase_transition: 'bg-gray-600 text-gray-200'
    };
    return colors[significance] || 'bg-gray-600 text-gray-200';
  };

  const getJudgmentColor = (judgment) => {
    const colors = {
      'Blunder': '#ffd6d6',
      'Mistake': '#fff7d6',
      'Inaccuracy': '#e6f4ff',
      'Good': '#f0f9e6',
      'Best': '#e6ffe6'
    };
    return colors[judgment] || 'transparent';
  };

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString || '';
    }
  };

  // Loading component
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-6 border border-gray-700 text-center max-w-md mx-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">{t("Analyzing Game")}</h2>
          <p className="text-gray-300">{t("Wait_for_analyzing")}</p>
        </div>
      </div>
    );
  }

  // No data component
  if (!analysis && !chatAnalytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-6 border border-gray-700 text-center max-w-md mx-4">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{t("No analysis data")}</h2>
          <p className="text-gray-300">{t("Chess analytics data is not available for this game.")}</p>
          {/* <button
            onClick={() => navigate("/main")}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white transition-colors"
          >
            {t("Main")}
          </button> */}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Chess pattern overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `linear-gradient(45deg, #ffffff1a 25%, transparent 25%), 
                           linear-gradient(-45deg, #ffffff1a 25%, transparent 25%), 
                           linear-gradient(45deg, transparent 75%, #ffffff1a 75%), 
                           linear-gradient(-45deg, transparent 75%, #ffffff1a 75%)`,
          backgroundSize: '60px 60px',
          backgroundPosition: '0 0, 0 30px, 30px -30px, -30px 0px'
        }} />
      </div>
      
      <div className="relative z-10 text-white p-3 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              {/* <button 
                onClick={() => navigate("/main")}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-700 hover:bg-gray-700 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="text-sm">{t("Main")}</span>
              </button> */}
             <div className="flex-1 flex justify-center">
                <h1 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  {t("Chess Game Analysis")}
                </h1>
              </div>
              <div className="w-20"></div>
            </div>
            <div className="text-center text-gray-300">
              <p className="text-sm lg:text-lg mb-2">{t("Psychological and Tactical Analysis")}</p>
              <p className="text-xs lg:text-base">
                {typeof opening === 'string' ? opening : opening?.name || ''} • {result}
              </p>
            </div>
          </div>

          {/* Game Info Cards - FIXED: Better mobile display */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-400 text-xs mb-1">{t("Players")}</p>
                  <p className="text-sm font-medium text-blue-400 truncate">{whitePlayer || 'White Player'}</p>
                  <p className="text-sm font-medium text-red-400 truncate">{blackPlayer || 'Black Player'}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-400 text-xs mb-1">{t("Game Time")}</p>
                  <p className="text-sm font-medium">{ gameMetadata?.startTime !== null ? formatDateTime(gameMetadata?.startTime) : t('N/A')}</p>
                  <p className="text-xs text-gray-300">{t("Duration")}: {gameMetadata.duration || t('N/A')}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <Timer className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-400 text-xs mb-1">{t("Time Control")}</p>
                  <p className="text-sm font-medium">{gameMetadata.gameType || 'Standard'}</p>
                  <p className="text-xs text-gray-300">{t("Total moves")}: {totalMoves || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <Gamepad2 className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-400 text-xs mb-1">{t("Result")}</p>
                  <p className="text-sm font-medium">{result || 'In Progress'}</p>
                  <p className="text-xs text-gray-300">{t("Opening")}: {(typeof opening === 'string' ? opening : opening?.name) || 'Unknown'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t("White Accuracy")}</p>
                  <p className="text-2xl font-bold text-blue-400">{whiteAccuracy}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t("Black Accuracy")}</p>
                  <p className="text-2xl font-bold text-red-400">{blackAccuracy}%</p>
                </div>
                <TrendingDown className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t("Total Moves")}</p>
                  <p className="text-2xl font-bold text-green-400">{totalMoves}</p>
                </div>
                <Clock className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t("Best move accuracy")}</p>
                  <p className="text-2xl font-bold text-yellow-400">{accuracyPercent}%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Player Performance Summary */}
          {statistics.summary && (
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700 mb-6">
              <h3 className="text-lg font-semibold mb-4">
                {t("Opening")}: {typeof opening === 'string' ? opening : opening?.name || ''} 
                {opening?.eco ? ` (${opening.eco})` : ''}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-400 mb-2">
                    {t("White")}: {whitePlayer}
                  </h4>
                  <div className="text-sm space-y-1">
                    <div>{t("Best")}: <span className="font-medium">{statistics.summary.w_bestMoves || 0}</span></div>
                    <div>{t("Good")}: <span className="font-medium">{statistics.summary.w_goodMoves || 0}</span></div>
                    <div>{t("Inaccuracies")}: <span className="font-medium">{statistics.summary.w_inaccuracies || 0}</span></div>
                    <div>{t("Mistakes")}: <span className="font-medium">{statistics.summary.w_mistakes || 0}</span></div>
                    <div>{t("Blunders")}: <span className="font-medium">{statistics.summary.w_blunders || 0}</span></div>
                    {w_acpl && <div>{t("ACPL")}: <span className="font-medium">{w_acpl}</span></div>}
                  </div>
                </div>
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-400 mb-2">
                    {t("Black")}: {blackPlayer}
                  </h4>
                  <div className="text-sm space-y-1">
                    <div>{t("Best")}: <span className="font-medium">{statistics.summary.b_bestMoves || 0}</span></div>
                    <div>{t("Good")}: <span className="font-medium">{statistics.summary.b_goodMoves || 0}</span></div>
                    <div>{t("Inaccuracies")}: <span className="font-medium">{statistics.summary.b_inaccuracies || 0}</span></div>
                    <div>{t("Mistakes")}: <span className="font-medium">{statistics.summary.b_mistakes || 0}</span></div>
                    <div>{t("Blunders")}: <span className="font-medium">{statistics.summary.b_blunders || 0}</span></div>
                    {b_acpl && <div>{t("ACPL")}: <span className="font-medium">{b_acpl}</span></div>}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-300">
                <div className="mb-2 font-medium">{t("Game summary")}</div>
                <div>
                  {t("Total moves")}: {totalMoves} | {t("Result")}: {result} | {t("Best move accuracy")}: {accuracyPercent}%
                </div>
                <div>
                  {t("Total blunders")}: {blunderCount}, {t("mistakes")}: {mistakeCount}, {t("inaccuracies")}: {inaccCount}, 
                  {t("good")}: {goodCount}, {t("best")}: {bestMoveCount}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Chess Board */}
            <div className="lg:col-span-1">
              <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-center">{t("Position")}</h3>
                <div className="flex justify-center items-center mb-4">
                  <div className="flex flex-col items-center">
                    <Chessboard
                      position={currentFen}
                      boardWidth={boardWidth}
                      arePiecesDraggable={false}
                      boardOrientation={gameInfo?.color || playerColor || 'white'}
                      customArrows={arrows}
                    />
                  </div>
                </div>
                
                {/* Board Controls */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <button 
                    onClick={onFirst} 
                    disabled={moveIndex === 0} 
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={onPrev} 
                    disabled={moveIndex === 0} 
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 bg-gray-700 rounded text-sm">
                    {moveIndex}/{(movePositions?.length || 1) - 1}
                  </span>
                  <button 
                    onClick={onNext} 
                    disabled={moveIndex === (movePositions?.length || 1) - 1} 
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={onLast} 
                    disabled={moveIndex === (movePositions?.length || 1) - 1} 
                    className="p-2 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Debug info - remove this later */}
                <div className="text-x6 text-gray-500 text-center mb-2">
                  {t("Current")}: {moveIndex} | {movePositions?.length || 0} | FEN: {currentFen ? 'OK' : 'Missing'}
                  {currentPosition?.san && <span> | {t("Move")}: {currentPosition.san}</span>}
                </div>

                {bestMoveFen && (
                  <button
                    onClick={() => setBestMoveFen(null)}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                  >
                    {t("Restore Original Position")}
                  </button>
                )}
              </div>
            </div>

            {/* Charts - Use chatAnalytics moveData for charts */}
            {chartMoveData && chartMoveData.length > 0 && (
              <div className="lg:col-span-2">
                <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
                  {/* Chart Tabs */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { id: 'confidence', label: t('Confidence') || 'Confidence', icon: TrendingUp },
                      { id: 'stress', label: t('Stress') || 'Stress', icon: AlertTriangle },
                      { id: 'time', label: t('Time') || 'Time', icon: Clock },
                      { id: 'accuracy', label: t('Accuracy') || 'Accuracy', icon: CheckCircle }
                    ].map(tab => {
                      const IconComponent = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                            activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <IconComponent className="w-4 h-4" />
                          <span className="text-sm">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Chart */}
                  <div className="w-full overflow-x-auto">
                    <div className="min-w-[600px]">
                      {renderChart()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Analysis Tables and Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Move Analysis Table */}
            {analysisArr && analysisArr.length > 0 && (
              <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ChevronRight className="w-5 h-5 text-blue-400" />
                  {t("Move Analysis")}
                </h3>
                <div className="overflow-x-auto max-h-80 border border-gray-600 rounded">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-700">
                      <tr>
                        <th className="text-left p-2 font-bold">#</th>
                        <th className="text-left p-2 font-bold">{t("Side")}</th>
                        <th className="text-left p-2 font-bold">{t("Move")}</th>
                        <th className="text-left p-2 font-bold">{t("Eval")}</th>
                        <th className="text-left p-2 font-bold">{t("Judgment")}</th>
                        <th className="text-left p-2 font-bold">{t("Best")}</th>
                        <th className="text-left p-2 font-bold">{t("Arrow")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisArr.map((analysis, i) => {
                        const currentMovePos = movePositions.find(pos => pos.halfMoveIndex === i + 1);
                        const isCurrentMove = moveIndex === i + 1;
                        
                        return (
                          <tr 
                            key={i}
                            className={`cursor-pointer hover:bg-gray-700 transition-colors ${
                              isCurrentMove ? 'bg-blue-900 border-l-4 border-blue-400' : ''
                            }`}
                            style={{ backgroundColor: !isCurrentMove ? getJudgmentColor(analysis.judgment?.name || analysis.judgment) + '33' : undefined }}
                            onClick={() => {
                              setMoveIndex(i + 1);
                              setBestMoveFen(null);
                              setArrows([]);
                            }}
                          >
                            <td className="p-2">{Math.floor(i / 2) + 1}</td>
                            <td className="p-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                i % 2 === 0 ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'
                              }`}>
                                {i % 2 === 0 ? t("White") : t("Black")}
                              </span>
                            </td>
                            <td className="p-2 font-medium">
                              {currentMovePos?.san || analysis.san || analysis.playedSan || analysis.playedUci || analysis.bestMoveUci || ""}
                            </td>
                            <td className="p-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                (analysis.evalAfter || 0) > 0 ? 'bg-green-600' : 
                                (analysis.evalAfter || 0) < 0 ? 'bg-red-600' : 'bg-gray-600'
                              }`}>
                                {analysis.evalAfter !== undefined
                                  ? (analysis.evalAfter / 100).toFixed(2)
                                  : analysis.eval !== undefined
                                  ? (analysis.eval / 100).toFixed(2)
                                  : analysis.evalBefore !== undefined
                                  ? (analysis.evalBefore / 100).toFixed(2)
                                  : "0.00"}
                              </span>
                            </td>
                            <td className="p-2">
                              {analysis.judgment ? (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  analysis.judgment === 'Blunder' ? 'bg-red-600' :
                                  analysis.judgment === 'Mistake' ? 'bg-orange-600' :
                                  analysis.judgment === 'Inaccuracy' ? 'bg-yellow-600' :
                                  analysis.judgment === 'Good' ? 'bg-green-600' :
                                  analysis.judgment === 'Best' || analysis.judgment === 'Excellent move!' ? 'bg-blue-600' : 'bg-gray-600'
                                }`}>
                                  {t(analysis.judgment)}
                                </span>
                              ) : ""}
                            </td>
                            <td className="p-2">
                              {analysis.bestMoveUci && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fen = getFenAfterBestMove(i);
                                    if (fen) setBestMoveFen(fen);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 underline font-bold text-xs"
                                >
                                  {analysis.bestMoveUci}
                                </button>
                              )}
                            </td>
                            <td className="p-2">
                              {analysis.bestMoveUci ? (
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const from = analysis.bestMoveUci.slice(0, 2);
                                      const to = analysis.bestMoveUci.slice(2, 4);
                                      const isSameArrow =
                                        arrows.length > 0 &&
                                        arrows[0][0] === from &&
                                        arrows[0][1] === to;

                                      setArrows(isSameArrow ? [] : [[from, to]]);
                                    }}
                                    className={`text-xs px-2 py-1 rounded ${
                                      arrows.length > 0 &&
                                      arrows[0][0] === analysis.bestMoveUci.slice(0, 2) &&
                                      arrows[0][1] === analysis.bestMoveUci.slice(2, 4)
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                    }`}
                                  >
                                    {arrows.length > 0 &&
                                    arrows[0][0] === analysis.bestMoveUci.slice(0, 2) &&
                                    arrows[0][1] === analysis.bestMoveUci.slice(2, 4)
                                      ? t("Hide Arrow")
                                      : t("Show Arrow")}
                                  </button>
                                  {/* {bestMoveFen && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setBestMoveFen(null);
                                        setArrows([]);
                                      }}
                                      className="text-xs px-2 py-1 rounded bg-purple-600 hover:bg-purple-700"
                                    >
                                      {t("Back to Game")}
                                    </button>
                                  )} */}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-center">
                  <p className="text-xs text-gray-400">{t("Click a row to jump to that position")}</p>
                </div>
              </div>
            )}

            {/* Enhanced Game Summary with ChatAnalytics support */}
            <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                {t("Game Summary")}
              </h3>
              
              {/* Key Moments */}
              {keyMoments && keyMoments.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">{t("Key Moments")}</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {keyMoments.map((moment, index) => (
                      <div key={index} className="bg-gray-700/50 p-2 rounded text-sm">
                        <div className="font-medium">{t("Move")} {moment.move}</div>
                        <div className="text-gray-300">{moment.event}</div>
                        <span className={`inline-block px-2 py-1 rounded text-xs mt-1 ${getSignificanceColor(moment.significance)}`}>
                          {moment.significance}
                        </span>
                        {moment.impact && <div className="text-gray-400 text-xs mt-1">{moment.impact}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {errors && errors.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-red-400 mb-2">{t("Critical Errors")}</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {errors.map((error, index) => (
                      <div key={index} className="bg-gray-700/50 p-2 rounded text-sm">
                        <div className="font-medium flex items-center gap-2">
                          {t("Move")} {error.move} - {error.player === 'white' ? t("White") : t("Black")}
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSeverityColor(error.severity) }} />
                        </div>
                        <div className="text-gray-300">{error.description}</div>
                        {error.betterMove && (
                          <div className="text-green-400 text-xs">{t("Better")}: {error.betterMove}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Basic Summary for non-chatAnalytics data */}
              {!chatAnalytics && (
                <div className="text-sm text-gray-300">
                  <div className="mb-2 font-medium">{t("Analysis Summary")}</div>
                  <div className="space-y-1">
                    <div>{t("Total moves")}: {totalMoves}</div>
                    <div>{t("Result")}: {result}</div>
                    <div>{t("Best moves")}: {bestMoveCount}</div>
                    <div>{t("Good moves")}: {goodCount}</div>
                    <div>{t("Inaccuracies")}: {inaccCount}</div>
                    <div>{t("Mistakes")}: {mistakeCount}</div>
                    <div>{t("Blunders")}: {blunderCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Conclusions - only for chatAnalytics */}
          {conclusions && Object.keys(conclusions).length > 0 && (
            <div className="mt-6 bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-sm rounded-lg p-6 border border-gray-600">
              <h3 className="text-xl font-semibold mb-4 text-center">{t("Game Conclusions")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {conclusions.whiteStrengths && (
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-2">{t("White Strengths")}:</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      {conclusions.whiteStrengths.map((strength, index) => (
                        <li key={index}>• {strength}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {conclusions.blackWeaknesses && (
                  <div>
                    <h4 className="font-semibold text-red-400 mb-2">{t("Black Weaknesses")}:</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      {conclusions.blackWeaknesses.map((weakness, index) => (
                        <li key={index}>• {weakness}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {conclusions.turningPoint && (
                <div className="mt-6 bg-gray-600/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-400 mb-2">{t("Turning Point")}:</h4>
                  <p className="text-gray-300 text-sm">
                    <span className="font-medium">{t("Move")} {conclusions.turningPoint.move}:</span> {conclusions.turningPoint.description}
                  </p>
                </div>
              )}

              {conclusions.gamePhases && (
                <div className="mt-6">
                  <h4 className="font-semibold text-center mb-4">{t("Game Phases Analysis")}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {conclusions.gamePhases.opening && (
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <h5 className="font-semibold text-green-400 mb-2">{t("Opening")}</h5>
                        <p className="text-gray-300 text-sm">{conclusions.gamePhases.opening}</p>
                      </div>
                    )}
                    {conclusions.gamePhases.middlegame && (
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <h5 className="font-semibold text-blue-400 mb-2">{t("Middlegame")}</h5>
                        <p className="text-gray-300 text-sm">{conclusions.gamePhases.middlegame}</p>
                      </div>
                    )}
                    {conclusions.gamePhases.endgame && (
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <h5 className="font-semibold text-purple-400 mb-2">{t("Endgame")}</h5>
                        <p className="text-gray-300 text-sm">{conclusions.gamePhases.endgame}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mobile-friendly bottom spacing and Main button */}
          <div className="flex justify-center py-8">
            <button 
              onClick={() => navigate("/main")}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg border border-blue-500 transition-colors text-white font-medium"
            >
              <Home className="w-5 h-5" />
              <span>{t("Main")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedChessAnalytics;